import { isRealDate } from '@/components/catalog/form-utils';

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * The field rules a revenue document's TWO editors share.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every record here — deal, quote, invoice — is now edited in two places: a
 * modal that fills the whole record at once, and an inline block on the detail
 * page that writes one column at a time. Both are checking the SAME database
 * column against the SAME server-side rule, so both need the same shape test
 * and, more importantly, the same SENTENCE about it: a person told
 * "Use up to 12 digits and at most 2 decimal places" in one place and something
 * else in the other has no way to know they were refused for one reason.
 *
 * ⚠️ **Do not re-declare any of this in a form.** This module exists because
 * the identical mistake has already been paid for once: `DOCUMENT_LINE_AMOUNT_OPTIONS`
 * was inlined in both document forms and the two labellings diverged — the
 * same customer-facing tax treatment worded two ways across the halves of one
 * conversion. `AMOUNT_PATTERN` and `CURRENCY_PATTERN` had
 * reached THREE copies (`DealFormModal`, `document-form.ts`, `InlineDealFields`)
 * before this module was cut, and one of the columns they guard is money.
 *
 * Same standing rule as `src/lib/inclusions.ts`: three copies of a rule agree
 * only until one of them is edited, and the one that drifts is always the one
 * nobody was looking at.
 *
 * ## Why `src/lib/` and not beside a form
 *
 * `@/components/documents/document-form` is the QUOTE-and-INVOICE vocabulary
 * module and re-exports the two patterns below, so every existing import of
 * them keeps working. It is not the right home for a rule a DEAL also obeys:
 * a deal is not one of those two forms, and shared rules in this app live in
 * `src/lib/` (`inclusions.ts`, `line-project.ts`, `deal-conversion.ts`).
 *
 * There is also a mechanical reason not to export these from a form: oxlint's
 * `react(only-export-components)` fires when a component module exports plain
 * functions, and this repo sits at zero such warnings. A `.ts` module in
 * `src/lib/` has no such problem, which is why the lift landed here rather than
 * as an export added to `DealFormModal`.
 *
 * ## Nothing here trims
 *
 * Each validator tests the string it is handed, exactly. The modals pass an
 * already-trimmed value (they trim once, into the payload as well as the
 * check); the inline editors pass the raw draft, because `InlineField` compares
 * the draft against the stored value to decide whether anything changed at all
 * and a trim inside a validator would not reach that comparison. Trimming here
 * would therefore change behaviour on one caller and not the other — a
 * whitespace-only amount would stop being refused inline and would be sent
 * verbatim to a 422 instead.
 */

/**
 * All four document totals and a deal's amount share one shape:
 * `^\d{1,12}(\.\d{1,2})?$`.
 *
 * ⚠️ Wider than a product's price, which allows 10 digits — do not "unify"
 * those two, they are different columns.
 *
 * Money is a decimal STRING and never sees a float, so the pattern is the whole
 * rule: no `Number()`, no rounding, no `?? 0`.
 */
export const AMOUNT_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;

/** `^[A-Z]{3}$`, checked exactly. "usd" is a 422 — the server does not fold case. */
export const CURRENCY_PATTERN = /^[A-Z]{3}$/;

/**
 * Server-enforced maximums on a DEAL, mirrored so a control stops where the API
 * would.
 *
 * ⚠️ **`description` (20,000) and `xeroQuoteId` (100) are deliberately absent,
 * and the absence is the record of why.** A maximum length is a property of a
 * CONTROL, so no entry here means no control anywhere — the same convention the
 * quote maximums below follow for their three reference columns. The owner
 * removed both from every deal surface on 2026-08-31, and both left
 * `DealCreateInput` with them, so nothing in this app can write either.
 *
 * ⚠️ The server still ACCEPTS both keys and still RETURNS both values —
 * `description` holds real data on live deals. Nothing was deleted; the
 * controls and the write were. Do not re-add an entry here to "restore" a
 * length: the length was never the thing that went.
 */
export const DEAL_MAX_LENGTHS = {
  dealName: 255,
  nextStep: 100,
} as const;

/**
 * Server-enforced maximums on a QUOTE.
 *
 * ⚠️ **Three reference columns are deliberately absent, and their absence is
 * the record of why nothing may write them.** A maximum length is a property of
 * a control, so no entry here means no control anywhere:
 *
 *  - **`quoteNumber` (64)** is allocated by the SERVER and is read-only on BOTH
 *    create and PATCH — measured on the wire: `422`, `path: "quoteNumber"`,
 *    *"allocated by the server"*. An entry for it
 *    survived in `InlineQuoteFields` after that field was made read-only there,
 *    pointing at a control that no longer existed; it is not coming back.
 *  - **`xeroQuoteNumber` (50)** IS written by a successful push, so a hand-typed
 *    box over it competes with a real identifier — and it goes stale on unlink,
 *    so it can name ANOTHER quote's document.
 *  - **`xeroQuoteId` (100)** is written by NOTHING on a quote, so a hand-typed
 *    GUID is the only thing that column will ever hold and it will look
 *    authoritative while meaning nothing.
 *
 * `xeroBrandingThemeId` is `CHAR(36)` and its value only ever comes from a
 * picker, so that entry is a floor under a stored value rather than something a
 * person can trip over.
 *
 * ⚠️ The two tracking overrides are deliberately WIDER than Xero's own
 * 50-character option limit. An over-long name must reach the server and come
 * back a 422 when it is checked against the live option list — not be quietly
 * truncated into a prefix that looks like a real option and matches nothing.
 */
export const QUOTE_MAX_LENGTHS = {
  subject: 255,
  /** The CUSTOMER's PO number. Never ours. */
  reference: 255,
  terms: 16_000,
  xeroBrandingThemeId: 36,
  xeroTrackingLocation: 100,
  xeroTrackingTourType: 100,
} as const;

/**
 * A deal's name. Not nullable server-side — `dealName: null` is a 422 — so an
 * emptied name is refused here rather than sent.
 */
export function validateDealName(value: string): string | null {
  if (value === '') return 'Deal name is required.';
  if (value.length > DEAL_MAX_LENGTHS.dealName) return 'Must be 255 characters or fewer.';
  return null;
}

/**
 * A deal's amount.
 *
 * `''` clears the column, and a cleared amount is not `"0.00"`: `null` means
 * nothing was ever recorded and `"0.00"` is a real price of zero.
 */
export function validateDealAmount(value: string): string | null {
  if (value === '') return null;
  if (!AMOUNT_PATTERN.test(value)) {
    return 'Use up to 12 digits and at most 2 decimal places, e.g. 18000.00.';
  }
  return null;
}

/** Uppercased before testing, and uppercased again on the wire — `"usd"` is a 422. */
export function validateDealCurrency(value: string): string | null {
  if (value.trim() === '') return null;
  if (!CURRENCY_PATTERN.test(value.trim().toUpperCase())) {
    return 'Use a three-letter ISO code, e.g. USD.';
  }
  return null;
}

/** Calendar-checked: `2026-02-30` matches the shape and is a 422. */
export function validateDealClosingDate(value: string): string | null {
  if (value === '') return null;
  if (!isRealDate(value)) return 'Must be a real date in YYYY-MM-DD form.';
  return null;
}

